import React, { FC, useEffect, useState } from 'react'
import {
  IonButton, IonCol, IonGrid, IonRow, IonLabel, IonItem, IonToggle,
} from '@ionic/react'
import { Background } from 'victory'
import axios from 'axios'
import { User } from '../../types/GlobalTypes'
import { SettingTitle } from '../StyledContainers'
import UserIcon from '../../../helpers/UserIcon'
import { useSettings } from '../../../hooks/settings/useSettings'
import { useWorkspace } from '../../../hooks/useWorkspace'
import { accessOptions, roleOptions } from './roles'

interface Props {
  user: User
  onBack: () => void
}

const Access: FC<Props> = ({ user, onBack }) => {
  const { selectedUserRoles } = useSettings()
  const { workspace } = useWorkspace()
  const [access, setAccess] = useState<Record<string, boolean>>(
    Object.fromEntries(accessOptions.map((o) => [o.value, false])),
  )

  const [roles, setRoles] = useState<Record<string, boolean>>(
    Object.fromEntries(roleOptions.map((o) => [o.value, false])),
  )

  useEffect(() => {
    const normalize = (s?: string) => (s ?? '').toLowerCase().trim()

    if (!Array.isArray(selectedUserRoles) || selectedUserRoles.length === 0) return

    const selected = new Set(selectedUserRoles.map(normalize))

    // Access map
    const accessMap = Object.fromEntries(
      accessOptions.map((o) => [o.value, selected.has(normalize(o.value))]),
    )
    setAccess(accessMap)

    // Roles map (new)
    const rolesMap = Object.fromEntries(
      roleOptions.map((o) => [o.value, selected.has(normalize(o.value))]),
    )
    setRoles(rolesMap)
  }, [selectedUserRoles])

  const handleSave = async () => {
    const role_types = Object.keys(roles).filter((value) => roles[value])
    const access_types = Object.keys(access).filter((value) => access[value])
    const roleAccess = [...role_types, ...access_types]
    console.log('Saving roles:', roleAccess)
    await axios.put('/api/v1/domain/manage_access', {
      domain_id: workspace?.id,
      user_id: user.id,
      role_types: roleAccess,
    })
    onBack()
  }

  return (
    <IonGrid style={{ height: '100%', overflowY: 'auto' }}>
      <IonRow>
        <IonCol style={{ textAlign: 'left', paddingBottom: '4px', borderBottom: '1px solid #ccc' }}>
          <h6 style={{
            color: '#000000',
            fontSize: '16px',
            fontWeight: 'bold',
            margin: '0 0 2px 0', // top-right-bottom-left
          }}
          >
            {`${user.first_name} ${user.final_name}`}
          </h6>
          <p style={{
            color: '#747474',
            fontSize: '13px',
            margin: '0 0 4px 0',
            paddingBottom: '4px',
            display: 'inline-block',
          }}
          >
            {user.email}
          </p>
        </IonCol>
      </IonRow>

      <IonRow style={{ flex: 1, overflow: 'hidden' }}>
        <IonCol size='5' style={{ padding: '10px 0' }}>
          <p style={{ color: '#326771', fontSize: 16, marginLeft: '2em' }}>Manage Workspace Access</p>
        </IonCol>
        <IonCol />
        <IonCol size='5' style={{ padding: '10px 0' }}>
          <p style={{ color: '#326771', fontSize: 16, marginRight: '2em' }}>Manage User Roles</p>
        </IonCol>
      </IonRow>

      <IonRow style={{ flex: 1, overflow: 'hidden' }}>
        <IonCol
          size='5'
          style={{
            background: 'white',
            border: '1px solid #747474',
            borderRadius: '2.5px',
            height: '100%',
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            marginLeft: '2em',
          }}
        >
          {accessOptions.map((opt) => (
            <IonItem
              key={opt.value}
              lines='none'
              className='ion-justify-content-between'
              style={{
                background: 'white',
                '--background': 'white',
                borderBottom: '1px solid #ccc',
                paddingTop: '4px',
                paddingBottom: '4px',
                '--padding-start': '0px',
                '--inner-padding-end': '0px',
                margin: 0,
              }}
            >
              <IonLabel style={{
                color: '#326771', fontSize: '14px', margin: 0, padding: 0,
              }}
              >
                {opt.label}
              </IonLabel>
              <IonToggle
                key={`${opt.value}:${access[opt.value] ? '1' : '0'}`} // ← force remount when it flips
                slot='end' // ← place at end; prevents odd item layout effects
                checked={!!access[opt.value]}
                onIonChange={(e) => setAccess({ ...access, [opt.value]: e.detail.checked })}
              />
            </IonItem>

          ))}
        </IonCol>
        <IonCol />
        <IonCol
          size='5'
          style={{
            background: 'white',
            border: '1px solid #747474',
            borderRadius: '2.5px',
            height: '100%',
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            marginRight: '2em',
          }}
        >
          {roleOptions.map((opt) => (
            <IonItem
              key={opt.value}
              lines='none'
              className='ion-justify-content-between'
              style={{
                background: 'white',
                '--background': 'white',
                borderBottom: '1px solid #ccc',
                paddingTop: '4px',
                paddingBottom: '4px',
                '--padding-start': '0px',
                '--inner-padding-end': '0px',
                margin: 0,
              }}
            >
              <IonLabel style={{ color: '#326771' }}>{opt.label}</IonLabel>
              <IonToggle
                key={`${opt.value}:${roles[opt.value] ? '1' : '0'}`} // force remount on change
                slot='end'
                checked={!!roles[opt.value]}
                onIonChange={(e) => setRoles({ ...roles, [opt.value]: e.detail.checked })}
              />
            </IonItem>
          ))}
        </IonCol>
      </IonRow>
      <IonRow className='ion-justify-content-end' style={{ marginTop: '20px' }}>
        <IonButton onClick={onBack} style={{ '--background': '#8E151F' }}>Cancel</IonButton>
        <IonButton onClick={handleSave} style={{ '--background': '#326771', marginLeft: '10px' }}>Save Changes</IonButton>
      </IonRow>
    </IonGrid>
  )
}

export default Access
